import { type QnAStateType, selectors } from '@uber/react-qna-lib';
import type { CachedQuestionnaireStatus } from '../../modernization-common/side-panel/atoms/questionnaire.atom';
import { convertAnswersToUserAnswerMap, partitionStaticAndDynamicQuestionnaire } from '../side-popover-panel/utils';
import type { AnswerT } from '../types/types';
export const validateQuestionnaireAnswers = (questionnaire: QnAStateType, answers?: AnswerT): boolean => {
  if (!answers || Object.keys(answers).length === 0) {
    return false;
  }
  try {
    const userAnswerMap = convertAnswersToUserAnswerMap(questionnaire, answers);
    const hasErrors = selectors.hasValidationErrors({
      ...questionnaire,
      answers: userAnswerMap
    });
    return !hasErrors;
  } catch (error: unknown) {
    console.error('Error validating questionnaire answers', error);
    // If validation fails due to conversion or other errors, consider invalid
    return false;
  }
};
export const getAnnotationQuestionnaireId = (annotation: {
  annotationLabel: {
    questionnaire?: Array<string | {
      questionnaireId: string;
    }>;
  };
}): string | undefined => {
  const hasQuestionnaire = (annotation.annotationLabel?.questionnaire?.length ?? 0) > 0;
  if (!hasQuestionnaire) {
    return;
  }
  const questionnaire = annotation.annotationLabel.questionnaire?.[0];
  const questionnaireId = typeof questionnaire === 'string' ? questionnaire : questionnaire?.questionnaireId;
  return questionnaireId;
};
export const isAnnotationPending = (annotation: {
  annotationLabel: {
    questionnaire?: Array<string | {
      questionnaireId: string;
    }>;
  };
  metadata?: {
    answers?: AnswerT;
  };
}, questionnaireCache: Record<string, CachedQuestionnaireStatus>): boolean => {
  const questionnaireId = getAnnotationQuestionnaireId(annotation);
  if (!questionnaireId) {
    return false;
  }
  const cachedQuestionnaire = questionnaireCache[questionnaireId];
  if (!cachedQuestionnaire || cachedQuestionnaire.status !== 'success') {
    // If questionnaire isn't cached, assume pending (should trigger fetch)
    return true;
  }
  const {
    staticQuestionnaire
  } = partitionStaticAndDynamicQuestionnaire(cachedQuestionnaire.questionnaire);
  if (staticQuestionnaire.topQuestionIds.length === 0) {
    return false;
  }
  if (annotation.metadata?.answers && Object.keys(annotation.metadata.answers).length === 0) {
    return true;
  }
  return !validateQuestionnaireAnswers(staticQuestionnaire, annotation.metadata?.answers);
};
export const isAnnotationWithQuestionnairePending = (annotations: {
  annotationLabel: {
    questionnaire?: Array<string | {
      questionnaireId: string;
    }>;
  };
  metadata?: {
    answers?: AnswerT;
  };
}[], questionnaireCache: Record<string, CachedQuestionnaireStatus>): boolean => {
  return annotations.some(annotation => {
    const questionnaireId = getAnnotationQuestionnaireId(annotation);
    if (!questionnaireId) {
      return false;
    }
    const cachedQuestionnaire = questionnaireCache[questionnaireId];
    if (!cachedQuestionnaire || cachedQuestionnaire.status !== 'success') {
      // If questionnaire isn't cached, assume pending (should trigger fetch)
      return true;
    }
    const {
      dynamicQuestionnaire,
      staticQuestionnaire
    } = partitionStaticAndDynamicQuestionnaire(cachedQuestionnaire.questionnaire);
    const dynamicQuestionnairePending = dynamicQuestionnaire.topQuestionIds.length !== 0 && !validateQuestionnaireAnswers(dynamicQuestionnaire, annotation.metadata?.answers);
    const staticQuestionnairePending = staticQuestionnaire.topQuestionIds.length !== 0 && !validateQuestionnaireAnswers(staticQuestionnaire, annotation.metadata?.answers);
    return dynamicQuestionnairePending || staticQuestionnairePending;
  });
};
export const isStaticAndDynamicQuestionnairePending = (annotations: {
  annotationLabel: {
    questionnaire?: Array<string | {
      questionnaireId: string;
    }>;
  };
  metadata?: {
    answers?: AnswerT;
  };
}[], questionnaireCache: Record<string, CachedQuestionnaireStatus>): {
  dynamicQuestionnairePending: boolean;
  staticQuestionnairePending: boolean;
} => {
  const results = annotations.map(annotation => {
    const questionnaireId = getAnnotationQuestionnaireId(annotation);
    if (!questionnaireId) {
      return {
        dynamicQuestionnairePending: false,
        staticQuestionnairePending: false
      };
    }
    const cachedQuestionnaire = questionnaireCache[questionnaireId];
    if (!cachedQuestionnaire || cachedQuestionnaire.status !== 'success') {
      // If questionnaire isn't cached, assume pending (should trigger fetch)
      return {
        dynamicQuestionnairePending: true,
        staticQuestionnairePending: true
      };
    }
    const {
      dynamicQuestionnaire,
      staticQuestionnaire
    } = partitionStaticAndDynamicQuestionnaire(cachedQuestionnaire.questionnaire);
    const dynamicQuestionnairePending = dynamicQuestionnaire.topQuestionIds.length !== 0 && !validateQuestionnaireAnswers(dynamicQuestionnaire, annotation.metadata?.answers);
    const staticQuestionnairePending = staticQuestionnaire.topQuestionIds.length !== 0 && !validateQuestionnaireAnswers(staticQuestionnaire, annotation.metadata?.answers);
    return {
      dynamicQuestionnairePending,
      staticQuestionnairePending
    };
  });

  // Aggregate results: if ANY annotation has pending questionnaires, mark as pending
  return {
    dynamicQuestionnairePending: results.some(result => result.dynamicQuestionnairePending),
    staticQuestionnairePending: results.some(result => result.staticQuestionnairePending)
  };
};